/* eslint-disable perfectionist/sort-imports */
import 'src/utils/highlight';

import ReactQuill from 'react-quill';

import { alpha } from '@mui/material/styles';

import { useId, useRef, useMemo, useState, useEffect, forwardRef } from 'react';
import { StyledEditor } from './styles';
import Toolbar, { formats } from './toolbar';
import { insertSanitizedPaste } from './sanitize-clipboard';
import { EditorProps } from './types';

// ----------------------------------------------------------------------

const Editor = forwardRef<ReactQuill, EditorProps>(({
  id = 'minimal-quill',
  error,
  simple = false,
  noHeader = false,
  helperText,
  disabled,
  sx,
  formats: formatsProp,
  ...other
}, ref) => {
  
  const quillRef = useRef<ReactQuill>(null);

  // Quill binds its toolbar by DOM node, never by `#id` selector. A selector goes through
  // `document.querySelector`, so when the same editor mounts twice — the application page and the
  // candidate preview both render the Executive Summary box — the second instance took over the
  // first one's toolbar and threw on start-up, which the root error boundary turned into the
  // full-screen error page (JT1-1726). A callback ref into state gives us the node; ReactQuill is
  // held back until it exists, because it reads `modules` in its own `componentDidMount`, which
  // runs before this component's effects.
  const [toolbarEl, setToolbarEl] = useState<HTMLDivElement | null>(null);

  // The rendered id only has to be unique so the markup stays valid across duplicate mounts —
  // nothing in the app reads it.
  const uid = useId();
  const domId = `${id}-${uid}`;

  // Memoised because react-quill deep-compares `modules` on every update and destroys/recreates the
  // editor when it differs; a fresh object each render would now also carry a DOM node into that
  // comparison.
  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbarEl,
      },
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: false,
      },
      syntax: true,
      clipboard: {
        matchVisual: false,
      },
    }),
    [toolbarEl]
  );

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();

    const handlePaste = (event: ClipboardEvent) => insertSanitizedPaste(quill, event);

    quill.root.addEventListener('paste', handlePaste);

    // eslint-disable-next-line consistent-return
    return () => {
      quill.root.removeEventListener('paste', handlePaste);
    };
    // The Quill ref is only populated once the toolbar node exists and ReactQuill mounts, a render
    // later than this component — so the listener has to be attached off the back of that.
  }, [toolbarEl]);

  return (
    <>
      <StyledEditor
        sx={{
          ...(error && {
            border: (theme) => `solid 1px ${theme.palette.error.main}`,
            '& .ql-editor': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }),
          ...sx,
        }}
      >
        <Toolbar ref={setToolbarEl} id={domId} simple noHeader={noHeader} />

        {toolbarEl && <ReactQuill
          ref={(instance) => {
            // Update internal ref
            (quillRef as React.MutableRefObject<ReactQuill | null>).current = instance;
            // Forward ref to parent
            if (ref) {
              if (typeof ref === 'function') {
                ref(instance);
              } else {
                (ref as React.MutableRefObject<ReactQuill | null>).current = instance;
              }
            }
          }}
          modules={modules}
          formats={formatsProp ?? formats}
          readOnly={disabled}
          theme="snow"
          placeholder="Write something awesome..."
          {...other}
        />}
      </StyledEditor>

      {helperText && helperText}
    </>
  );
});

Editor.displayName = 'Editor';

export default Editor;
